import { t } from "ttag";

import {
  PeoplePageWrapper,
  PeopleSection,
} from "metabase/admin/components/PeopleSection";
import { useListPermissionsGroupsQuery } from "metabase/api";
import { AdminPaneLayout } from "metabase/common/components/AdminPaneLayout";
import Button from "metabase/common/components/Button";
import Link from "metabase/common/components/Link";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { color } from "metabase/lib/colors";
import { useSelector } from "metabase/lib/redux";
import * as Urls from "metabase/lib/urls";
import { getUser, getUserIsAdmin } from "metabase/selectors/user";
import { Box, Icon, Input } from "metabase/ui";

import { PeopleList } from "../components/PeopleList";
import { PeopleNav } from "../components/PeopleNav";
import { USER_STATUS } from "../constants";
import { usePeopleQuery } from "../hooks/use-people-query";

import S from "./PeopleListingApp.module.css";

const PAGE_SIZE = 25;

export function PeopleListingApp({ children }: { children: React.ReactNode }) {
  const isAdmin = useSelector(getUserIsAdmin);
  const currentUser = useSelector(getUser);

  const {
    data: groups = [],
    isLoading,
    error,
  } = useListPermissionsGroupsQuery();

  const {
    query,
    status,
    searchInputValue,
    updateSearchInputValue,
    updateStatus,
    handleNextPage,
    handlePreviousPage,
  } = usePeopleQuery(PAGE_SIZE);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSearchInputValue(e.target.value);
  };

  const showInviteButton = isAdmin && status === USER_STATUS.active;

  const headingContent = (
    <div className={S.Header}>
      <div className={S.HeaderTop}>
        <div style={{display: "flex", alignItems: "center", gap: "1rem"}}>
          <Box style={{backgroundColor: color("admin-navbar"), borderRadius: "999px", width: "3rem", height: "3rem", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer"}} onClick={() => history.back()}>
            <Icon name="arrow_left" size={12} color="white" />
          </Box>
          <div className={S.PeopleNavWrapper}>
            <PeopleNav isPeoplePage />
          </div>
        </div>
        <div style={{justifySelf: "end", display: "flex", alignItems: "center", gap: "1rem"}}>
          <button
            type="button"
            className={
              status === USER_STATUS.active
                ? `${S.StatusToggle} ${S.StatusToggleActive}`
                : S.StatusToggle
            }
            onClick={() => updateStatus(USER_STATUS.active)}
            aria-pressed={status === USER_STATUS.active}
          >{"Active"}</button>
          <button
            type="button"
            className={
              status === USER_STATUS.deactivated
                ? `${S.StatusToggle} ${S.StatusToggleActive}`
                : S.StatusToggle
            }
            onClick={() => updateStatus(USER_STATUS.deactivated)}
            aria-pressed={status === USER_STATUS.deactivated}
          >{"Deactivated"}</button>
        </div>
      </div>

      <div className={S.HeaderBottom}>
        <div className={S.SearchPill}>
          <div className={S.SearchIconWrapper}>
            <Icon c="white" name="search" size={14} />
          </div>
          <Input
            className={S.SearchInput}
            fz="sm"
            radius="xl"
            type="text"
            placeholder={t`Search people...`}
            value={searchInputValue}
            onChange={handleSearchChange}
            rightSectionPointerEvents="all"
            rightSection={
              searchInputValue === "" ? (
                <div />
              ) : (
                <Input.ClearButton
                  c={"text-secondary"}
                  onClick={() => updateSearchInputValue("")}
                />
              )
            }
          />
        </div>

        {showInviteButton && (
          <Link to={Urls.newUser()} className={S.InviteLink}>
            <Button primary className={S.InviteButton}>
              <span className={S.InviteButtonIcon}>
                <Icon name="add_circle" size={18} />
              </span>
              <span>{t`Invite User`}</span>
            </Button>
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <PeoplePageWrapper>
      <PeopleSection>
        <LoadingAndErrorWrapper
          error={error}
          loading={isLoading || !currentUser}
          style={{
            width: "100%"
          }}
        >
          <AdminPaneLayout isPeopleListing headingContent={headingContent}>
            {currentUser && (
              <PeopleList
                groups={groups}
                isAdmin={isAdmin}
                currentUser={currentUser}
                query={query}
                onNextPage={handleNextPage}
                onPreviousPage={handlePreviousPage}
              />
            )}
            {children}
          </AdminPaneLayout>
        </LoadingAndErrorWrapper>
      </PeopleSection>
    </PeoplePageWrapper>
  );
}
